import { Injectable } from '@angular/core';
import { debounceTime, Observable, Subject, Subscription } from 'rxjs';
import {
  AbstractControl,
  FormArray,
  FormControl,
  FormGroup,
} from '@angular/forms';
import { isNil, Nillable } from '@web/utils';

export class Change {
  public constructor(
    private readonly data: {
      path: string;
      control: FormControl;
    },
  ) {}

  public get path(): string {
    return this.data.path;
  }

  public get control(): FormControl {
    return this.data.control;
  }
}

@Injectable({ providedIn: 'root' })
export class FormChangeWatcherService {
  private changeSubject: Nillable<Subject<Change>> = new Subject<Change>();
  private subscriptions: Nillable<Subscription> = new Subscription();

  private readonly debounceTime = 400;

  public watch(form: FormGroup): Observable<Change> {
    this.changeSubject = new Subject<Change>();
    this.subscriptions = new Subscription();

    this.registerListener(form);

    return this.changeSubject.asObservable();
  }

  public destroy(): void {
    if (!isNil(this.changeSubject)) {
      this.changeSubject.complete();
    }

    if (!isNil(this.subscriptions)) {
      this.subscriptions.unsubscribe();
    }

    this.changeSubject = null;
    this.subscriptions = null;
  }

  private registerListener(control: AbstractControl, path = ''): void {
    if (control instanceof FormGroup) {
      Object.entries(control.controls).forEach(([key, childControl]) => {
        const newPath = path ? `${path}.${key}` : key;

        this.registerListener(childControl, newPath);
      });
    } else if (control instanceof FormArray) {
      control.controls.forEach((childControl, index) => {
        const newPath = `${path}[${index}]`;

        this.registerListener(childControl, newPath);
      });
    } else if (control instanceof FormControl) {
      if (isNil(this.subscriptions)) {
        return;
      }

      this.subscriptions.add(
        control.valueChanges
          .pipe(debounceTime(this.debounceTime))
          .subscribe(() => {
            this.changeSubject?.next(new Change({ control, path }));
          }),
      );
    }
  }
}
